import { DEFAULT_PAGE_SIZE } from "@/shared/constants";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { flatten } from "lodash";
import { useMemo } from "react";
import followsApi from "@/api/follows/client";

export interface Props {
  artistId: string;
}

export const useInfiniteFollowers = ({ artistId }: Props) => {
  const { data: paginationData } = useQuery({
    queryKey: ["followers-count", artistId],
    queryFn: async () => {
      const totalCount = await followsApi.getFollowersCount(artistId);
      const totalPages = Math.ceil(totalCount / DEFAULT_PAGE_SIZE);

      return { totalCount, totalPages };
    },
  });

  const { data, hasNextPage, fetchNextPage } = useInfiniteQuery(
    ["artist-followers", artistId],
    async ({ pageParam = 0 }) => {
      const followsWithFollowers = await followsApi.getFollowersByUser(
        artistId,
        pageParam * DEFAULT_PAGE_SIZE,
        DEFAULT_PAGE_SIZE,
      );

      return {
        follows: followsWithFollowers.map((f) => f.follower),
        nextPage: pageParam + 1,
      };
    },
    {
      getNextPageParam: (page) => {
        return paginationData?.totalPages &&
          page.nextPage < paginationData.totalPages
          ? page.nextPage
          : undefined;
      },
      enabled: !!paginationData?.totalPages,
    },
  );

  const follows = useMemo(() => {
    return data ? flatten(data.pages.map((page) => page.follows)) : [];
  }, [data]);

  return {
    follows,
    totalFollows: paginationData?.totalCount || 0,
    hasNextPage,
    fetchNextPage,
  };
};

export const useInfiniteFollowing = ({ artistId }: Props) => {
  const { data: paginationData } = useQuery({
    queryKey: ["following-count", artistId],
    queryFn: async () => {
      const totalCount = await followsApi.getFollowingCount(artistId);
      const totalPages = Math.ceil(totalCount / DEFAULT_PAGE_SIZE);

      return { totalCount, totalPages };
    },
  });

  const { data, hasNextPage, fetchNextPage } = useInfiniteQuery(
    ["artist-following", artistId],
    async ({ pageParam = 0 }) => {
      const followsWithFollowees = await followsApi.getFolloweesByUser(
        artistId,
        DEFAULT_PAGE_SIZE * pageParam,
        DEFAULT_PAGE_SIZE,
      );
      return {
        follows: followsWithFollowees.map((f) => f.folowee),
        nextPage: pageParam + 1,
      };
    },
    {
      getNextPageParam: (page) => {
        return paginationData?.totalPages &&
          page.nextPage < paginationData.totalPages
          ? page.nextPage
          : undefined;
      },
      enabled: !!paginationData?.totalPages,
    },
  );

  const follows = useMemo(() => {
    return data ? flatten(data.pages.map((page) => page.follows)) : [];
  }, [data]);

  return {
    follows,
    totalFollows: paginationData?.totalCount || 0,
    hasNextPage,
    fetchNextPage,
  };
};
